import { useEffect, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import type { Agent, AgentSchedule } from '@/types'
import { listAgentSchedules, runHeartbeat } from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { StatusDot } from '@/components/ui/StatusDot'
import { PendingBackendNotice } from '@/components/ui/EmptyState'

export function AgentSchedulesTab() {
  const { agent } = useOutletContext<{ agent: Agent }>()
  const [schedules, setSchedules] = useState<AgentSchedule[]>([])
  const [running, setRunning] = useState(false)
  const [heartbeatResult, setHeartbeatResult] = useState<string | null>(null)

  useEffect(() => {
    listAgentSchedules(agent.id).then(setSchedules)
  }, [agent.id])

  async function handleRunHeartbeat() {
    setRunning(true)
    setHeartbeatResult(null)
    try {
      const res = await runHeartbeat(agent.id)
      setHeartbeatResult(res.dry_run ? 'Dry-run — sin backend real conectado todavía' : 'Ejecutado')
    } catch (e) {
      setHeartbeatResult(e instanceof Error ? e.message : 'Error')
    } finally {
      setRunning(false)
    }
  }

  const columns: Column<AgentSchedule>[] = [
    { header: 'Nombre', cell: (s) => s.name },
    { header: 'Cron', cell: (s) => s.cron_expression, mono: true },
    { header: 'Canal', cell: (s) => s.channel ?? '—' },
    { header: 'Estado', cell: (s) => s.enabled ? <StatusDot status="ok" showLabel={false} /> : <StatusDot status="inactive" showLabel={false} /> },
    { header: 'Últ. status', cell: (s) => s.last_status ?? '—' },
    { header: 'Runs', cell: (s) => String(s.run_count), mono: true },
  ]

  return (
    <div>
      <PendingBackendNotice endpoint={`GET /agents/${agent.id}/schedules`} />

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>Heartbeat</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
              {agent.heartbeat ? 'Documento configurado' : 'Sin documento de heartbeat'}
            </div>
          </div>
          <button
            onClick={handleRunHeartbeat}
            disabled={running || !agent.heartbeat}
            style={{
              background: 'var(--surface-raised)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-primary)',
              borderRadius: 'var(--radius-sm)',
              padding: '8px 16px',
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            {running ? 'Corriendo…' : 'Correr ahora'}
          </button>
        </div>
        {heartbeatResult && (
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>{heartbeatResult}</div>
        )}
      </Card>

      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 8 }}>Schedules</div>
      <DataTable columns={columns} rows={schedules} getRowKey={(s) => s.id} />
    </div>
  )
}
